import { useCallback, useEffect, useState } from 'react'
import { api } from './api'
import type {
  CanadaGapFarmFormKey,
  CanadaGapFarmStatus,
  CanadaGapFormTable,
  CanadaGapFormKey,
  CanadaGapFormStatus,
  CanadaGapH1Export,
  CanadaGapH2Export,
  CanadaGapHarvestCheck,
  CanadaGapP2Export,
  CanadaGapStatus,
  CanadaGapBlockingApplication,
  CanadaGapEntryIssue,
  CanadaGapViolation,
} from './types'

const farmBase = (orgId: string, farmId: string) => `/orgs/${orgId}/farms/${farmId}/canadagap`

/** Live CanadaGAP completeness for one field and season. Call `reload` after any log changes. */
export function useCanadaGapStatus(
  orgId: string | undefined,
  farmId: string | undefined,
  fieldId: string | undefined,
  seasonYear: number,
) {
  const [status, setStatus] = useState<CanadaGapStatus | null>(null)

  const reload = useCallback(async () => {
    if (!orgId || !farmId || !fieldId) return
    try {
      setStatus(await api.get<CanadaGapStatus>(`${farmBase(orgId, farmId)}/status?fieldId=${fieldId}&seasonYear=${seasonYear}`))
    } catch {
      // Status is advisory: a failure leaves the badges in their static form rather than blocking the page.
      setStatus(null)
    }
  }, [orgId, farmId, fieldId, seasonYear])

  useEffect(() => {
    reload()
  }, [reload])

  return { status, reload }
}

const shortDate = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })

function describeIssue(issue: CanadaGapEntryIssue): string[] {
  const lines = issue.violations.map((v) => `${shortDate(issue.date)} · ${v.message}`)
  if (issue.missing.length > 0) lines.push(`${shortDate(issue.date)} · ${issue.label} — missing ${issue.missing.join(', ')}`)
  return lines
}

/** Forms H1 and H2 share the season header (crops, variety, date planted); P2 does not. */
const USES_SEASON_HEADER: Record<CanadaGapFormKey, boolean> = { h1: true, h2Fertilizer: true, h2Amendment: true, p2: false }

/** Props for a CanadaGAP ComplianceBadge from the live status, or {} while it is loading. */
export function canadaGapBadgeProps(status: CanadaGapStatus | null, form: CanadaGapFormKey) {
  if (!status) return {}
  const f = status.forms[form]
  const headerMissing = USES_SEASON_HEADER[form] ? status.header.missing : []
  return {
    entryCount: f.entryCount,
    incompleteCount: f.incompleteCount,
    violationCount: f.violationCount,
    issues: [...(headerMissing.length ? [`Season details — missing ${headerMissing.join(', ')}`] : []), ...f.issues.flatMap(describeIssue)],
  }
}

/** The issue for one log entry, if it has any. */
export function issueFor(form: CanadaGapFormStatus | undefined, entryId: string): CanadaGapEntryIssue | undefined {
  return form?.issues.find((i) => i.entryId === entryId)
}

/** Live CanadaGAP status of the farm-level forms (G, I, J, K, L, M) for one year. */
export function useCanadaGapFarmStatus(orgId: string | undefined, farmId: string | undefined, year: number) {
  const [status, setStatus] = useState<CanadaGapFarmStatus | null>(null)

  const reload = useCallback(async () => {
    if (!orgId || !farmId) return
    try {
      setStatus(await api.get<CanadaGapFarmStatus>(`${farmBase(orgId, farmId)}/farm-status?year=${year}`))
    } catch {
      setStatus(null)
    }
  }, [orgId, farmId, year])

  useEffect(() => {
    reload()
  }, [reload])

  return { status, reload }
}

const monthName = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { month: 'short' })

/** What a missed cadence period is called on each form. */
const CADENCE_LABEL: Partial<Record<CanadaGapFarmFormKey, (periods: string[]) => string>> = {
  g: (p) => `no inspection in ${p.map(monthName).join(', ')}`,
  m: (p) => `no pest check in ${p.map(monthName).join(', ')}`,
  j: (p) => `no check in the week${p.length > 1 ? 's' : ''} of ${p.map(shortDate).join(', ')}`,
  k: () => 'not held this year',
}

/** Missed-cadence lines for a farm-level form, e.g. "Packing shed — no inspection in Jun, Sep". */
export function cadenceLines(status: CanadaGapFarmStatus | null, form: CanadaGapFarmFormKey): string[] {
  const label = CADENCE_LABEL[form]
  if (!status || !label) return []
  return status.forms[form].cadenceGaps.map((g) => `${g.name} — ${label(g.periods)}`)
}

/** Props for a farm-level CanadaGAP ComplianceBadge, or {} while the status is loading. */
export function farmBadgeProps(status: CanadaGapFarmStatus | null, form: CanadaGapFarmFormKey) {
  if (!status) return {}
  const f = status.forms[form]
  return {
    entryCount: f.entryCount,
    incompleteCount: f.incompleteCount,
    violationCount: f.violationCount,
    issues: [...cadenceLines(status, form), ...f.issues.flatMap(describeIssue)],
  }
}

/** A missing column placed on the input that fills it; `product` is an H1 product's position in the request. */
export type CanadaGapInputGap = { input: string; product?: number; label: string }

/**
 * What the CanadaGAP rules say about one entry: every missing form column, the violations, and the
 * missing columns that belong to a single input.
 */
export type CanadaGapCheckResult = {
  missing: string[]
  violations: CanadaGapViolation[]
  inputs: CanadaGapInputGap[]
  /** H1 only: each product against its approved label, in order. */
  labelChecks?: LabelCheck[]
}

/** One label figure exceeded (a violation, red) or worth a look (a note, yellow). */
export type LabelFinding = { code: string; tone: 'violation' | 'note'; message: string }

/**
 * A product against its approved label: `pass` when every check that could run passed (rate and PHI
 * included), `fail` when a limit is exceeded, `unknown` when there is no checked label or not
 * enough to compare (`reason` says which).
 */
export type LabelCheck = { status: 'pass' | 'fail' | 'unknown'; findings: LabelFinding[]; reason?: string }

/** An unsaved entry to check. Blank values are fine; P2 also needs the field for its EAHD cross-check. */
export type CanadaGapCheckRequest =
  | { form: Exclude<CanadaGapFormKey, 'p2'> | CanadaGapFarmFormKey; entry: Record<string, unknown> }
  | { form: 'p2'; fieldId: string; entry: Record<string, unknown> }

/** A form's number input as a draft value: blank is null. */
export const draftNumber = (s: string) => (s.trim() === '' ? null : Number(s))

/**
 * Live CanadaGAP check of an entry while its form is filled in, re-run shortly after the draft stops
 * changing. Null until the first result, and whenever the check fails: it is advisory and never
 * blocks saving.
 */
export function useEntryCheck(orgId: string | undefined, farmId: string | undefined, request: CanadaGapCheckRequest, delayMs = 400) {
  const [result, setResult] = useState<CanadaGapCheckResult | null>(null)
  const body = JSON.stringify(request)

  useEffect(() => {
    if (!orgId || !farmId) return
    let cancelled = false
    const timer = setTimeout(() => {
      api
        .post<CanadaGapCheckResult>(`${farmBase(orgId, farmId)}/check`, JSON.parse(body))
        .then((r) => !cancelled && setResult(r))
        .catch(() => !cancelled && setResult(null))
    }, delayMs)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [orgId, farmId, body, delayMs])

  return result
}

/**
 * The same check for several unsaved entries at once (one per field, say), re-run shortly after any
 * of them stops changing. Results are in request order; null until the first answer, or when a
 * check fails (advisory, as above).
 */
export function useEntryChecks(orgId: string | undefined, farmId: string | undefined, requests: CanadaGapCheckRequest[], delayMs = 400) {
  const [results, setResults] = useState<(CanadaGapCheckResult | null)[] | null>(null)
  const body = JSON.stringify(requests)

  useEffect(() => {
    if (!orgId || !farmId) return
    let cancelled = false
    const timer = setTimeout(() => {
      const sent = JSON.parse(body) as CanadaGapCheckRequest[]
      Promise.all(sent.map((r) => api.post<CanadaGapCheckResult>(`${farmBase(orgId, farmId)}/check`, r).catch(() => null))).then((r) => !cancelled && setResults(r))
    }, delayMs)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [orgId, farmId, body, delayMs])

  return results
}

/** One application that constrains a harvest, in words: inside its waiting period, or no PHI recorded. */
export function describeBlocking(b: CanadaGapBlockingApplication): string {
  return b.eahd === null
    ? `${b.productName} (${b.source}) was applied ${b.appliedOn} with no PHI or harvest date recorded, so the waiting period can't be checked.`
    : `${b.productName} (${b.source}) was applied ${b.appliedOn}. Its waiting period ends ${b.eahd}.`
}

export async function checkHarvest(orgId: string, farmId: string, fieldId: string, harvestedOn: string) {
  return api.get<CanadaGapHarvestCheck>(`${farmBase(orgId, farmId)}/harvest-check?fieldId=${fieldId}&harvestedOn=${harvestedOn}`)
}

// ---------------------------------------------------------------------------
// Form exports (CSV laid out like the CanadaGAP form)
// ---------------------------------------------------------------------------

type Cell = string | number | boolean | null | undefined

function csvEscape(v: Cell): string {
  const s = v == null ? '' : String(v)
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

const line = (...cells: Cell[]) => cells.map(csvEscape).join(',')
const yesNo = (v: boolean | null) => (v == null ? '' : v ? 'Yes' : 'No')
const amount = (n: number | null, unit: string | null) => (n == null ? '' : `${n}${unit ? ` ${unit}` : ''}`)
const slug = (s: string) => s.trim().replace(/\s+/g, '-')

function downloadCsv(lines: string[], filename: string) {
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

const FOOTER = ['', line('Confirmation Signature:', '', 'Date:', '')]

function siteHeader(h: CanadaGapH1Export['header']): string[] {
  return [
    line('Operation Name:', h.operationName, 'Season:', h.seasonYear),
    line('Previous Year Crop(s):', h.previousCrop, 'Current Crop:', h.currentCrop),
    line('Production Site Information:', h.productionSite, 'Production Site Area:', amount(h.productionSiteArea, h.productionSiteAreaUnit)),
    line('Date Planted:', h.datePlanted, 'Variety:', h.variety),
  ]
}

export async function exportH1(orgId: string, farmId: string, fieldId: string, seasonYear: number) {
  const { header, rows } = await api.get<CanadaGapH1Export>(`${farmBase(orgId, farmId)}/forms/h1?fieldId=${fieldId}&seasonYear=${seasonYear}`)
  downloadCsv(
    [
      'CanadaGAP Form H1 - Agronomic Inputs (Agricultural Chemicals)',
      ...siteHeader(header),
      '',
      line(
        'Application Date', 'Product / Trade Name', 'PCP #', 'Actual Quantity Used', 'Rate Applied Per Unit',
        'Label Instructions Followed', 'Area / Quantity Treated', 'Method of Application',
        'Earliest Allowable Harvest Date (EAHD)', 'PHI (days)', 'Weather Conditions',
        'Signature of Applicator or Custom Application Invoice Attached',
      ),
      ...rows.map((r) =>
        line(
          r.appliedOn, r.productName, r.pcpNumber, amount(r.quantityUsed, r.quantityUnit), amount(r.rateApplied, r.rateUnit),
          yesNo(r.labelFollowed), amount(r.areaTreated, r.areaUnit), r.applicationMethod,
          r.eahd, r.phiDays, r.weatherConditions,
          r.customInvoiceAttached ? `Custom invoice attached${r.applicatorName ? ` (${r.applicatorName})` : ''}` : r.applicatorName,
        ),
      ),
      ...FOOTER,
    ],
    `H1-${slug(header.productionSite)}-${seasonYear}.csv`,
  )
}

export async function exportH2(orgId: string, farmId: string, fieldId: string, seasonYear: number) {
  const { header, fertilizerRows, amendmentRows } = await api.get<CanadaGapH2Export>(
    `${farmBase(orgId, farmId)}/forms/h2?fieldId=${fieldId}&seasonYear=${seasonYear}`,
  )
  downloadCsv(
    [
      'CanadaGAP Form H2 - Agronomic Inputs (Other)',
      ...siteHeader(header),
      '',
      'Section 1 - Commercial Fertilizer Application',
      line('Date', 'Blend', 'Rate', 'Fertilizer Lot # (if applicable)', "Applicator's Name"),
      ...fertilizerRows.map((r) => line(r.appliedOn, r.blend, amount(r.rateAmount, r.rateUnit), r.lotNumber, r.applicatorName)),
      '',
      'Section 2 - Manure / Compost / Compost Tea / Other By-Products / Pulp Sludge / Soil Amendment / Mulch and Row Cover',
      line('Date', 'What is Applied', 'Type', "Supplier's Name", 'Rate', 'Earliest Allowable Harvest Date', "Applicator's Name"),
      ...amendmentRows.map((r) =>
        line(r.appliedOn, r.substance, r.substanceType, r.supplierName, amount(r.rateAmount, r.rateUnit), r.eahd, r.applicatorName),
      ),
      ...FOOTER,
    ],
    `H2-${slug(header.productionSite)}-${seasonYear}.csv`,
  )
}

/** Form P2 is farm-wide; pass a fieldId to limit it to one production site. */
export async function exportP2(orgId: string, farmId: string, seasonYear: number, fieldId?: string) {
  const { header, rows } = await api.get<CanadaGapP2Export>(
    `${farmBase(orgId, farmId)}/forms/p2?seasonYear=${seasonYear}${fieldId ? `&fieldId=${fieldId}` : ''}`,
  )
  downloadCsv(
    [
      'CanadaGAP Form P2 - Harvesting and Storing Product (All Crops Except Potatoes)',
      line('Operation Name:', header.operationName, 'Season:', header.seasonYear),
      line('Completed by:', '', 'Date:', ''),
      '',
      line(
        'Product and Variety', 'PHI/EAHD/DAA met - Forms H1, H2 and H3 verified', 'Production site was assessed',
        'Harvest Date', 'Quantity / Units Harvested', 'Field/Block # / Pallet/Bin Tag', 'Packaging Materials Used',
        'Date Product Put into Storage', 'Farmula PHI check',
      ),
      ...rows.map((r) =>
        line(
          r.productVariety, yesNo(r.phiVerified), yesNo(r.siteAssessed), r.harvestedOn,
          amount(r.quantityHarvested, r.quantityUnit), r.productionSite, r.packagingMaterials, r.storedOn,
          r.phiCheck.ok
            ? 'OK'
            : r.phiCheck.blocking.map((b) => (b.eahd === null ? `No EAHD/PHI: ${b.productName}` : `Before EAHD: ${b.productName} (${b.eahd})`)).join('; '),
        ),
      ),
      ...FOOTER,
    ],
    `P2-${slug(header.operationName)}-${seasonYear}.csv`,
  )
}

/** A farm-level form (G, I, J, K, L, M) for a year as CSV, in the form's column order. */
export async function exportFarmForm(orgId: string, farmId: string, form: CanadaGapFarmFormKey, year: number, name?: string) {
  const query = new URLSearchParams({ year: String(year) })
  if (name) query.set('name', name)
  const { header, columns, rows } = await api.get<CanadaGapFormTable>(`${farmBase(orgId, farmId)}/forms/${form}?${query}`)
  downloadCsv(
    [
      `CanadaGAP Form ${header.form}`,
      line('Operation Name:', header.operationName, 'Year:', header.year, ...(header.name ? ['For:', header.name] : [])),
      '',
      line(...columns),
      ...rows.map((r) => line(...r)),
      ...FOOTER,
    ],
    `${form.toUpperCase()}-${slug(header.operationName)}${header.name ? `-${slug(header.name)}` : ''}-${year}.csv`,
  )
}
